import { useMemo } from "react";
import {
  Coins, Receipt, UsersThree, ShieldCheck, Bank, ChartLineUp,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, Area, ComposedChart,
} from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtCurrency } from "@/lib/format";

const cards = [
  { key: "headline_tax_drag_saved", label: "Tax drag saved", sub: "vs. corporate passive taxation", icon: Coins },
  { key: "headline_interest_recaptured", label: "Interest recaptured", sub: "from fleet lenders", icon: Receipt },
  { key: "headline_retention_value", label: "Retention value", sub: "golden handcuffs", icon: UsersThree },
  { key: "headline_ibc_cash_value", label: "IBC cash value", sub: "tax-deferred", icon: ShieldCheck },
];

const CHART_MARGIN = { top: 10, right: 20, left: 0, bottom: 10 };
const AXIS_TICK_X = { fill: "#6B7264", fontSize: 12 };
const AXIS_TICK_Y = { fill: "#6B7264", fontSize: 11 };
const AXIS_LINE = { stroke: "#E5E2D9" };
const X_AXIS_LABEL = { value: "Year", position: "insideBottom", offset: -4, fill: "#6B7264", fontSize: 11 };
const yTickFormatter = (v) => fmtCurrency(v, { compact: true });

const TABLE_HEADERS = [
  "Year", "Bank (after-tax)", "IBC Cash Value", "Tax Drag Saved",
  "Interest Recaptured", "Retention Value", "Total Wealth Gain",
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-brand-line rounded-md shadow-md p-3 text-xs font-sans">
      <div className="font-semibold text-brand-forest mb-2">Year {label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
            <span className="text-brand-ink/70">{p.name}</span>
          </span>
          <span className="font-semibold text-brand-ink">{fmtCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function ResultsDashboard({ result, clientLabel }) {
  const chartData = useMemo(
    () => result.yearly.map((y) => ({
      year: y.year,
      "Bank (after-tax)": y.bank_value,
      "IBC Policy": y.ibc_value,
      "Total Wealth Gain": y.total_wealth_gain,
    })),
    [result]
  );

  return (
    <div data-testid="results-dashboard" id="pdf-root" className="space-y-8">
      <HeadlineSection result={result} clientLabel={clientLabel} />
      <BreakdownCards result={result} />
      <ProjectionChart chartData={chartData} />
      <ProjectionTable yearly={result.yearly} />
      <DisclaimerBlock inputs={result.inputs} />
    </div>
  );
}

function HeadlineSection({ result, clientLabel }) {
  return (
    <section className="bg-brand-forest text-brand-bone rounded-md overflow-hidden relative grain">
      <div className="relative p-8 md:p-10 space-y-8">
        <div>
          <div className="flex items-center gap-2 text-brand-bone/60 text-xs uppercase tracking-[0.2em] font-semibold">
            <Bank size={14} weight="duotone" /> {result.inputs.horizon_years}-year projection
            {clientLabel && <span>· {clientLabel}</span>}
          </div>
          <div className="mt-5 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] text-brand-bone break-words">
            {fmtCurrency(result.headline_wealth_gain, { compact: false })}
          </div>
          <p className="mt-4 text-brand-bone/75 text-base md:text-lg max-w-xl leading-relaxed">
            That's the total projected wealth gain from moving the business from the
            "borrow from the bank" path to the "build a bank" path.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="headline-subcards">
          <SubCard label="5-yr gain" value={result.summary_5yr.total_wealth_gain} />
          <SubCard label="10-yr gain" value={result.summary_10yr.total_wealth_gain} />
          <SubCard label="Bank path (horizon)" value={result.headline_bank_value} dim />
          <SubCard label="IBC path (horizon)" value={result.headline_ibc_cash_value} accent />
        </div>
      </div>
    </section>
  );
}

function BreakdownCards({ result }) {
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {cards.map(({ key, label, sub, icon: Icon }, i) => (
        <div
          key={key}
          data-testid={`breakdown-${key}`}
          className="bg-white border border-brand-line rounded-md p-6 transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          <div className="flex items-start justify-between mb-5">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-brand-cream text-brand-forest">
              <Icon size={20} weight="duotone" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">{`0${i + 1}`}</span>
          </div>
          <div className="label-eyebrow">{label}</div>
          <div className="mt-2 font-serif text-3xl text-brand-forest">
            {fmtCurrency(result[key])}
          </div>
          <div className="mt-2 text-xs text-brand-muted">{sub}</div>
        </div>
      ))}
    </section>
  );
}

function ProjectionChart({ chartData }) {
  return (
    <section className="bg-white border border-brand-line rounded-md p-6 md:p-8">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <span className="label-eyebrow">Projection</span>
          <h3 className="mt-2 font-serif text-3xl text-brand-forest flex items-center gap-3">
            <ChartLineUp size={26} weight="duotone" /> Bank vs. IBC — year by year
          </h3>
        </div>
        <div className="flex gap-5 text-xs font-semibold">
          <Legend3 color="#8A9488" label="Bank (after-tax)" />
          <Legend3 color="#113C2B" label="IBC Policy" />
          <Legend3 color="#B8860B" label="Total Wealth Gain" />
        </div>
      </div>
      <div className="h-[380px]" data-testid="growth-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid stroke="#E5E2D9" vertical={false} />
            <XAxis
              dataKey="year"
              tick={AXIS_TICK_X}
              tickLine={false}
              axisLine={AXIS_LINE}
              label={X_AXIS_LABEL}
            />
            <YAxis
              tick={AXIS_TICK_Y}
              tickLine={false}
              axisLine={AXIS_LINE}
              tickFormatter={yTickFormatter}
              width={85}
            />
            <Tooltip content={<CustomTooltip />} />
            <defs>
              <linearGradient id="gainFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#B8860B" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#B8860B" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="Total Wealth Gain"
              stroke="#B8860B"
              strokeWidth={2}
              fill="url(#gainFill)"
            />
            <Line type="monotone" dataKey="Bank (after-tax)" stroke="#8A9488" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="IBC Policy" stroke="#113C2B" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function ProjectionTable({ yearly }) {
  return (
    <section className="bg-white border border-brand-line rounded-md overflow-hidden">
      <div className="p-6 md:p-8 border-b border-brand-line">
        <span className="label-eyebrow">Year-by-year</span>
        <h3 className="mt-2 font-serif text-3xl text-brand-forest">Detailed projection</h3>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="projection-table">
          <TableHeader className="bg-brand-cream/60 sticky top-0">
            <TableRow>
              {TABLE_HEADERS.map((h, i) => (
                <TableHead
                  key={h}
                  className={`text-brand-forest font-semibold ${i > 0 ? "text-right" : ""}`}
                >
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {yearly.map((r, i) => (
              <TableRow
                key={r.year}
                data-testid={`table-row-${r.year}`}
                className={i % 2 === 0 ? "bg-white" : "bg-brand-bone"}
              >
                <TableCell className="font-semibold text-brand-forest">{r.year}</TableCell>
                <TableCell className="text-right tabular-nums text-brand-ink/70">{fmtCurrency(r.bank_value)}</TableCell>
                <TableCell className="text-right tabular-nums text-brand-forest font-semibold">{fmtCurrency(r.ibc_value)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(r.tax_drag_saved)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(r.interest_recaptured)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(r.retention_value)}</TableCell>
                <TableCell className="text-right tabular-nums text-brand-copper font-bold">{fmtCurrency(r.total_wealth_gain)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function DisclaimerBlock({ inputs }) {
  return (
    <section
      data-testid="disclaimer-block"
      className="bg-brand-cream/60 border border-brand-line rounded-md p-6 md:p-8"
    >
      <div className="flex items-start gap-3 mb-3">
        <span className="label-eyebrow text-brand-forest">Important Disclosure</span>
      </div>
      <div className="space-y-3 text-xs leading-relaxed text-brand-ink/75 max-w-4xl">
        <p>
          This calculator produces a <strong>hypothetical illustration only</strong> and is not a contract, offer,
          or guarantee of any specific result. Figures are projections based solely on the inputs and
          assumptions you have entered, including a passive-income tax rate of {(inputs.passive_tax_rate * 100).toFixed(2)}%,
          an assumed dividend rate of {(inputs.dividend_rate * 100).toFixed(2)}%, and a baseline corporate
          return of {(inputs.passive_return_rate * 100).toFixed(2)}%.
        </p>
        <p>
          <strong>Dividends are not guaranteed.</strong> Participating life insurance dividend scales are declared
          annually by the issuing insurer at its sole discretion and may be higher or lower than the values shown.
          The 2026 dividend scale references (6.25%–6.40%) are illustrative and are not a commitment by any insurer.
        </p>
        <p>
          <strong>Tax assumptions may change.</strong> Canadian and Ontario tax rules — including CCPC passive
          income taxation, the small-business deduction, and integration treatment — are subject to legislative
          and regulatory change. Actual tax outcomes depend on the corporation's full tax position and require
          review by a qualified tax professional.
        </p>
        <p>
          <strong>Interest-recapture and retention figures are estimates</strong> based on simplified assumptions
          (uniform amortization, fixed turnover-cost factor, and a retention probability boost selected by the
          user). Real-world outcomes will differ.
        </p>
        <p>
          This tool does <strong>not</strong> constitute legal, accounting, tax, or insurance advice. It is a
          conversation aid only. Any insurance product referenced (including any "Infinite Banking Concept"
          implementation) must be evaluated against the actual policy contract, illustration, and suitability
          review provided by a licensed life-insurance advisor in the applicable jurisdiction. Clients should
          obtain independent professional advice before making financial decisions.
        </p>
        <p className="pt-2 border-t border-brand-line text-brand-muted">
          Generated {new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })} ·
          Build A Bank Instead of Borrow from Bank · For advisor use with Canadian (Ontario) CCPC clients.
        </p>
      </div>
    </section>
  );
}

function SubCard({ label, value, dim, accent }) {
  return (
    <div className={`rounded-md p-4 border ${accent ? "bg-brand-copper/20 border-brand-copper/40" : "bg-brand-bone/10 border-brand-bone/20"}`}>
      <div className={`text-[10px] uppercase tracking-[0.16em] ${dim ? "text-brand-bone/40" : "text-brand-bone/60"}`}>{label}</div>
      <div className={`mt-1.5 font-serif text-2xl ${valueColor(accent, dim)}`}>
        {fmtCurrency(value)}
      </div>
    </div>
  );
}

function valueColor(accent, dim) {
  if (accent) return "text-brand-copper";
  if (dim) return "text-brand-bone/60";
  return "text-brand-bone";
}

function Legend3({ color, label }) {
  return (
    <span className="flex items-center gap-2 text-brand-ink/70">
      <span className="w-3 h-[3px] rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
